import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  ChevronLeft,
  Phone,
  Mail,
  Calendar,
  Activity,
  Pill,
  FileText,
  Plus,
  Edit,
  AlertCircle,
  Heart,
  Droplet,
  User,
  Clock,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import api, { getLocalizedValue } from '@/services/api';

const PatientDetailPage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addRecordOpen, setAddRecordOpen] = useState(false);
  const [newRecord, setNewRecord] = useState({ type: '', notes: '', diagnosis: '' });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchPatientDetail();
  }, [isAuthenticated, navigate, id]);

  const fetchPatientDetail = async () => {
    setLoading(true);
    try {
      // Fetch patient data
      const patientRes = await api.get(`/patients/${id}`, {
        params: {
          'with': 'user;medicalHistory;appointments.appointmentStatus'
        }
      });
      
      const patientData = patientRes.data?.data || patientRes.data || {};
      
      // Get name
      const firstName = getLocalizedValue(patientData.first_name, lang) || patientData.first_name?.en || '';
      const lastName = getLocalizedValue(patientData.last_name, lang) || patientData.last_name?.en || '';
      const name = `${firstName} ${lastName}`.trim() || (lang === 'ar' ? 'مريض' : 'Patient');
      
      // Calculate age from birthday
      let age = null;
      if (patientData.birthday) {
        const birthDate = new Date(patientData.birthday);
        const today = new Date();
        age = today.getFullYear() - birthDate.getFullYear();
      }
      
      // Get medical history
      const medicalHistory = patientData.medicalHistory || {};
      
      // Transform appointments to visits
      const appointments = patientData.appointments || [];
      const visits = appointments.map(apt => ({
        id: apt.id,
        date: new Date(apt.appointment_at || apt.created_at),
        type: apt.appointmentStatus?.status || (lang === 'ar' ? 'موعد' : 'Appointment'),
        notes: apt.notes || '',
        diagnosis: apt.diagnosis || '',
      }));
      
      const transformedPatient = {
        id: patientData.id,
        name,
        phone: patientData.mobile || patientData.phone || '',
        email: patientData.user?.email || '',
        image: patientData.media?.[0]?.url || patientData.user?.avatar || null,
        age,
        gender: patientData.gender === 'male' 
          ? (lang === 'ar' ? 'ذكر' : 'Male')
          : patientData.gender === 'female'
          ? (lang === 'ar' ? 'أنثى' : 'Female')
          : '',
        bloodType: patientData.blood_type || medicalHistory.blood_type || '',
        height: medicalHistory.height ? `${medicalHistory.height} cm` : '',
        weight: medicalHistory.weight ? `${medicalHistory.weight} kg` : '',
        dateOfBirth: patientData.birthday || '',
        address: getLocalizedValue(patientData.address, lang) || patientData.address || '',
        emergencyContact: medicalHistory.emergency_contact || null,
        chronicDiseases: (medicalHistory.chronic_diseases || []).map((disease, idx) => ({
          id: idx + 1,
          name: typeof disease === 'object' ? disease.name : disease,
          since: disease.since || '',
          status: disease.status || 'monitoring'
        })),
        allergies: (medicalHistory.allergies || []).map((allergy, idx) => ({
          id: idx + 1,
          name: typeof allergy === 'object' ? allergy.name : allergy,
          severity: allergy.severity || 'medium'
        })),
        medications: (medicalHistory.permanent_medications || medicalHistory.medications || []).map((med, idx) => ({
          id: idx + 1,
          name: typeof med === 'object' ? med.name : med,
          dosage: med.dosage || '',
          frequency: med.frequency || ''
        })),
        visits,
      };
      
      setPatient(transformedPatient);
      
    } catch (error) {
      console.error('Failed to fetch patient:', error);
      toast.error(lang === 'ar' ? 'فشل في تحميل بيانات المريض' : 'Failed to load patient data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecord = async () => {
    if (!newRecord.type) {
      toast.error(lang === 'ar' ? 'يرجى اختيار نوع الزيارة' : 'Please select visit type');
      return;
    }
    
    try {
      // API call to add consultation record
      await api.post('/doctor/consultations', {
        patient_id: id,
        type: newRecord.type,
        notes: newRecord.notes,
        diagnosis: newRecord.diagnosis,
      });
      
      toast.success(lang === 'ar' ? 'تم إضافة السجل بنجاح' : 'Record added successfully');
      setAddRecordOpen(false);
      setNewRecord({ type: '', notes: '', diagnosis: '' });
      fetchPatientDetail(); // Refresh data
    } catch (error) {
      console.error('Failed to add record:', error);
      toast.error(lang === 'ar' ? 'فشل في إضافة السجل' : 'Failed to add record');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'medium': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      default: return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    }
  };

  if (!isAuthenticated) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-6">
        <div className="container mx-auto max-w-4xl space-y-6">
          <Skeleton className="h-48" />
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <User className="h-16 w-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
          <p className="text-slate-500 dark:text-slate-400">
            {lang === 'ar' ? 'المريض غير موجود' : 'Patient not found'}
          </p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/doctor/patients')}>
            {lang === 'ar' ? 'العودة للقائمة' : 'Back to List'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="patient-detail">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-6 max-w-4xl">
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate('/doctor/patients')}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button variant="outline" size="icon" onClick={fetchPatientDetail}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          
          <div className="flex items-center gap-6">
            <Avatar className="h-20 w-20">
              <AvatarImage src={patient.image} />
              <AvatarFallback className="bg-teal-100 text-teal-700 dark:bg-teal-900 dark:text-teal-300 text-2xl">
                {patient.name.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{patient.name}</h1>
              <div className="flex items-center gap-4 mt-2 text-slate-500 dark:text-slate-400">
                {patient.age && (
                  <span>{patient.age} {lang === 'ar' ? 'سنة' : 'years'}</span>
                )}
                {patient.gender && (
                  <>
                    <span>•</span>
                    <span>{patient.gender}</span>
                  </>
                )}
                {patient.bloodType && (
                  <>
                    <span>•</span>
                    <Badge variant="outline" className="font-bold">
                      <Droplet className="h-3 w-3 mr-1" />
                      {patient.bloodType}
                    </Badge>
                  </>
                )}
              </div>
              <div className="flex items-center gap-4 mt-3">
                {patient.phone && (
                  <a href={`tel:${patient.phone}`} className="text-sm text-teal-600 dark:text-teal-400 flex items-center gap-1 hover:underline">
                    <Phone className="h-4 w-4" />
                    {patient.phone}
                  </a>
                )}
                {patient.email && (
                  <a href={`mailto:${patient.email}`} className="text-sm text-teal-600 dark:text-teal-400 flex items-center gap-1 hover:underline">
                    <Mail className="h-4 w-4" />
                    {patient.email}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="mb-6 w-full justify-start">
            <TabsTrigger value="overview">{lang === 'ar' ? 'نظرة عامة' : 'Overview'}</TabsTrigger>
            <TabsTrigger value="history">{lang === 'ar' ? 'السجل الطبي' : 'Medical History'}</TabsTrigger>
            <TabsTrigger value="visits">{lang === 'ar' ? 'الزيارات' : 'Visits'}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Quick Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {patient.height && (
                <Card className="dark:bg-slate-800 border-0 shadow-sm">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'الطول' : 'Height'}</p>
                    <p className="text-lg font-bold text-slate-900 dark:text-white">{patient.height}</p>
                  </CardContent>
                </Card>
              )}
              {patient.weight && (
                <Card className="dark:bg-slate-800 border-0 shadow-sm">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'الوزن' : 'Weight'}</p>
                    <p className="text-lg font-bold text-slate-900 dark:text-white">{patient.weight}</p>
                  </CardContent>
                </Card>
              )}
              <Card className="dark:bg-slate-800 border-0 shadow-sm">
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'الزيارات' : 'Visits'}</p>
                  <p className="text-lg font-bold text-slate-900 dark:text-white">{patient.visits.length}</p>
                </CardContent>
              </Card>
              {patient.dateOfBirth && (
                <Card className="dark:bg-slate-800 border-0 shadow-sm">
                  <CardContent className="p-4 text-center">
                    <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'تاريخ الميلاد' : 'Birth Date'}</p>
                    <p className="text-lg font-bold text-slate-900 dark:text-white">
                      {format(new Date(patient.dateOfBirth), 'PP', { locale })}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Chronic Diseases */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="h-5 w-5 text-red-500" />
                  {lang === 'ar' ? 'الأمراض المزمنة' : 'Chronic Diseases'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {patient.chronicDiseases.length === 0 ? (
                  <p className="text-slate-500 dark:text-slate-400 text-center py-4">
                    {lang === 'ar' ? 'لا توجد أمراض مزمنة مسجلة' : 'No chronic diseases recorded'}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {patient.chronicDiseases.map(disease => (
                      <div key={disease.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{disease.name}</p>
                          {disease.since && (
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              {lang === 'ar' ? `منذ ${disease.since}` : `Since ${disease.since}`}
                            </p>
                          )}
                        </div>
                        <Badge className={disease.status === 'controlled' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                          {disease.status === 'controlled' 
                            ? (lang === 'ar' ? 'متحكم به' : 'Controlled')
                            : (lang === 'ar' ? 'تحت المراقبة' : 'Monitoring')
                          }
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Allergies */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                  {lang === 'ar' ? 'الحساسية' : 'Allergies'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {patient.allergies.length === 0 ? (
                  <p className="text-slate-500 dark:text-slate-400 text-center py-4">
                    {lang === 'ar' ? 'لا توجد حساسية مسجلة' : 'No allergies recorded'}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {patient.allergies.map(allergy => (
                      <Badge key={allergy.id} className={getSeverityColor(allergy.severity)}>
                        {allergy.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="space-y-6">
            {/* Medications */}
            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Pill className="h-5 w-5 text-blue-500" />
                  {lang === 'ar' ? 'الأدوية الحالية' : 'Current Medications'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {patient.medications.length === 0 ? (
                  <p className="text-slate-500 dark:text-slate-400 text-center py-4">
                    {lang === 'ar' ? 'لا توجد أدوية مسجلة' : 'No medications recorded'}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {patient.medications.map(med => (
                      <div key={med.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{med.name}</p>
                          {med.dosage && (
                            <p className="text-sm text-slate-500 dark:text-slate-400">{med.dosage}</p>
                          )}
                        </div>
                        {med.frequency && (
                          <Badge variant="outline">{med.frequency}</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="visits" className="space-y-6">
            <Card className="dark:bg-slate-800 border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {lang === 'ar' ? 'سجل الزيارات' : 'Visit History'}
                </CardTitle>
                <Dialog open={addRecordOpen} onOpenChange={setAddRecordOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-teal-600 hover:bg-teal-700">
                      <Plus className="h-4 w-4 mr-1" />
                      {lang === 'ar' ? 'إضافة سجل' : 'Add Record'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="dark:bg-slate-800">
                    <DialogHeader>
                      <DialogTitle>{lang === 'ar' ? 'إضافة سجل جديد' : 'Add New Record'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>{lang === 'ar' ? 'نوع الزيارة' : 'Visit Type'}</Label>
                        <Select value={newRecord.type} onValueChange={(v) => setNewRecord({...newRecord, type: v})}>
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder={lang === 'ar' ? 'اختر النوع' : 'Select type'} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="checkup">{lang === 'ar' ? 'فحص عام' : 'General Checkup'}</SelectItem>
                            <SelectItem value="followup">{lang === 'ar' ? 'متابعة' : 'Follow-up'}</SelectItem>
                            <SelectItem value="consultation">{lang === 'ar' ? 'استشارة' : 'Consultation'}</SelectItem>
                            <SelectItem value="emergency">{lang === 'ar' ? 'طوارئ' : 'Emergency'}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>{lang === 'ar' ? 'الملاحظات' : 'Notes'}</Label>
                        <Textarea 
                          value={newRecord.notes}
                          onChange={(e) => setNewRecord({...newRecord, notes: e.target.value})}
                          placeholder={lang === 'ar' ? 'أدخل الملاحظات...' : 'Enter notes...'}
                          className="mt-2"
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label>{lang === 'ar' ? 'التشخيص' : 'Diagnosis'}</Label>
                        <Input 
                          value={newRecord.diagnosis}
                          onChange={(e) => setNewRecord({...newRecord, diagnosis: e.target.value})}
                          placeholder={lang === 'ar' ? 'أدخل التشخيص...' : 'Enter diagnosis...'}
                          className="mt-2"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setAddRecordOpen(false)}>
                        {lang === 'ar' ? 'إلغاء' : 'Cancel'}
                      </Button>
                      <Button onClick={handleAddRecord} className="bg-teal-600 hover:bg-teal-700">
                        {lang === 'ar' ? 'حفظ' : 'Save'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {patient.visits.length === 0 ? (
                  <p className="text-slate-500 dark:text-slate-400 text-center py-8">
                    {lang === 'ar' ? 'لا توجد زيارات مسجلة' : 'No visits recorded'}
                  </p>
                ) : (
                  <div className="space-y-4">
                    {patient.visits.map((visit, index) => (
                      <div key={visit.id} className="relative pl-6 pb-6 border-l-2 dark:border-slate-700 last:pb-0">
                        <div className="absolute -left-2 top-0 w-4 h-4 bg-teal-500 rounded-full" />
                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <Badge variant="outline">{visit.type}</Badge>
                            <span className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {format(visit.date, 'PPp', { locale })}
                            </span>
                          </div>
                          {visit.notes && (
                            <p className="text-slate-600 dark:text-slate-300 text-sm mt-2">{visit.notes}</p>
                          )}
                          {visit.diagnosis && (
                            <p className="text-teal-600 dark:text-teal-400 text-sm mt-2 font-medium">
                              {lang === 'ar' ? 'التشخيص:' : 'Diagnosis:'} {visit.diagnosis}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PatientDetailPage;
